import React from "react";
import { v4 as uuidv4 } from "uuid";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { FormProvider, useForm } from "react-hook-form";
import { TbArrowBigRightFilled } from "react-icons/tb";

import { useToaster } from "utils";
import { PaziaDateSelect, PaziaSelect } from "components/atoms";
import { PaziaModal } from "components/molecules";
import { formatDate } from "utils";
import { CourseSessionApi, courseSessionsApi } from "api";
import { CourseTrancheApi } from "api/types";
import { useCourseTranches } from "api/hooks/useCourseTranches";
import { queryClient } from "main";
import { QUERIES_KEYS } from "queries";
import { PaziaFormButton } from "components/atoms";
import { PaziaInput } from "components/atoms";
import { ModalHeader } from "../Modals/ModalHeader";

interface EditSessionModalProps {
  onCloseRequest: () => Promise<void>;
  courseId: number;
  session?: {
    dateFrom: Date;
    dateTo: Date;
    id: number;
    name: string;
  };
  // All sessions of the course — used to work out which tranches belong to the edited session.
  sessions: CourseSessionApi[];
}

export const EditSessionModal = ({ onCloseRequest, courseId, session, sessions }: EditSessionModalProps) => {
  return (
    <PaziaModal
      id="edit-session-modal"
      onCloseRequest={onCloseRequest}
      isOpen={session != null}
      size="xl"
      className="p-4"
    >
      {session && (
        <EditSessionModalForm
          onCloseRequest={onCloseRequest}
          courseId={courseId}
          session={session}
          sessions={sessions}
        />
      )}
    </PaziaModal>
  );
};

export const EditSessionModalForm = ({
  onCloseRequest,
  courseId,
  session,
  sessions,
}: Required<EditSessionModalProps>) => {
  const { showSuccess, showError } = useToaster();
  const { data: tranches } = useCourseTranches(courseId);
  const datedTranches = tranches.filter((t) => !!t.dueDate);
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      [FORM_FIELDS.NAME]: session.name,
      [FORM_FIELDS.NEW_FROM_DATE]: session.dateFrom,
      [FORM_FIELDS.NEW_TO_DATE]: session.dateTo,
    },
  });

  const onSubmitHandler = async (data: SessionFormValues) => {
    const { newFromDate, newToDate, name, trancheDueDates: newDueDates, ...rest } = data;
    const mappings = Object.entries(rest)
      .filter(([_, value]) => !!value)
      .map(([key, value]) => ({ from: key, to: value + "" }));
    const trancheDueDates = datedTranches
      .map((t) => ({ tranche: t, newDueDate: newDueDates?.[trancheFieldKey(t.id)] }))
      .filter(({ tranche, newDueDate }) => newDueDate && !isSameDay(newDueDate, tranche.dueDate))
      .map(({ tranche, newDueDate }) => ({ trancheId: tranche.id, dueDate: atNoon(newDueDate!) }));

    try {
      await courseSessionsApi.update(courseId, session.id, { name, newFromDate, newToDate, mappings, trancheDueDates });
      if (trancheDueDates.length > 0)
        await queryClient.invalidateQueries({ queryKey: QUERIES_KEYS.COURSE_TRANCHES(courseId) });
      onCloseRequest();
      reset();
      showSuccess("Zaktualizowano zjazd.");
    } catch (error) {
      showError("Nie udało się zaktualizować zjazdu.");
    }
  };
  const { handleSubmit, reset, watch, setValue } = methods;

  const newFromDate = watch(FORM_FIELDS.NEW_FROM_DATE);
  const newToDate = watch(FORM_FIELDS.NEW_TO_DATE);
  const name = watch(FORM_FIELDS.NAME);
  const newToDateIsBeforeNewFromDate = newToDate < newFromDate;

  // Suggest moving only the tranches that fall between the previous session's end and this
  // session's end, by the same number of days as the session start. When a copied course is
  // moved by more than its own span (e.g. a year ahead), tranches already moved while mapping an
  // earlier session fall outside that window, so they aren't suggested twice. For shorter shifts
  // they can be — it's only a prefill, the admin sees and can correct every date.
  const shiftDays = newFromDate ? daysBetween(session.dateFrom, newFromDate) : 0;
  const previousSessionEnd = sessions
    .filter((s) => s.id !== session.id && startOfDay(s.dateTo) < startOfDay(session.dateFrom))
    .reduce<Date | null>((latest, s) => (!latest || s.dateTo > latest ? s.dateTo : latest), null);
  const isSuggested = (tranche: CourseTrancheApi) =>
    (!previousSessionEnd || startOfDay(tranche.dueDate) > startOfDay(previousSessionEnd)) &&
    startOfDay(tranche.dueDate) <= startOfDay(session.dateTo);

  React.useEffect(() => {
    datedTranches.forEach((t) => {
      const suggested = isSuggested(t) && !Number.isNaN(shiftDays) ? addDays(t.dueDate, shiftDays) : t.dueDate;
      setValue(`${FORM_FIELDS.TRANCHE_DUE_DATES}.${trancheFieldKey(t.id)}`, new Date(suggested));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shiftDays, tranches]);
  return (
    <FormProvider {...methods}>
      <ModalHeader>Edycja zjazdu</ModalHeader>
      <form onSubmit={handleSubmit(onSubmitHandler)}>
        <div className="grid grid-cols-2 gap-6">
          <div className="col-span-2">
            <PaziaInput name="name" label="Nazwa" />
          </div>
          <DateBox label="START" name={FORM_FIELDS.NEW_FROM_DATE} date={session.dateFrom} />
          <DateBox label="KONIEC" name={FORM_FIELDS.NEW_TO_DATE} date={session.dateTo} />
        </div>
        {(name != session.name || (newFromDate && newToDate && !newToDateIsBeforeNewFromDate)) && (
          <div className="pt-4">
            <div className="pb-2 text-center text-sm font-semibold uppercase">Mapowanie dni</div>
            <DatesMapping
              dateFrom={session.dateFrom}
              dateTo={session.dateTo}
              newFromDate={newFromDate}
              newToDate={newToDate}
            >
              {datedTranches.length > 0 && (
                <TrancheDueDatesMapping tranches={datedTranches} isSuggested={isSuggested} shiftDays={shiftDays} />
              )}
            </DatesMapping>
          </div>
        )}
        {newToDateIsBeforeNewFromDate && (
          <div className="pt-4 text-center text-sm text-red-500">Data zakończenia przed rozpoczęciem</div>
        )}
      </form>
    </FormProvider>
  );
};

type DateBoxProps = {
  label: string;
  name: string;
  date: Date;
};
const DateBox = ({ label, name, date }: DateBoxProps) => {
  return (
    <div className="flex flex-col items-center gap-2 bg-primary-50 p-4 dark:bg-slate-700">
      <label className="font-semibold uppercase text-black dark:text-white">{label}</label>
      <span className="opacity-80">{formatDate(date)}</span>
      <PaziaDateSelect name={name} label="Nowa data rozpoczęcia" />
    </div>
  );
};

const FORM_FIELDS = {
  NAME: "name",
  NEW_FROM_DATE: "newFromDate",
  NEW_TO_DATE: "newToDate",
  TRANCHE_DUE_DATES: "trancheDueDates",
} as const;

// Prefixed so react-hook-form treats the key as an object property, not an array index.
const trancheFieldKey = (trancheId: number) => `t${trancheId}`;

const schema = yup
  .object({
    [FORM_FIELDS.NAME]: yup.string().required(),
    [FORM_FIELDS.TRANCHE_DUE_DATES]: yup.mixed<Record<string, Date | null>>(),
    [FORM_FIELDS.NEW_FROM_DATE]: yup.date().required(),
    [FORM_FIELDS.NEW_TO_DATE]: yup
      .date()
      .required()
      .min(yup.ref(FORM_FIELDS.NEW_FROM_DATE), "Data zakończenia przed rozpoczęciem"),
  })
  .required();

export type SessionFormValues = yup.InferType<typeof schema>;

const DatesMapping = ({
  dateFrom,
  dateTo,
  newFromDate,
  newToDate,
  children,
}: {
  dateFrom: Date;
  dateTo: Date;
  newFromDate: Date;
  newToDate: Date;
  children?: React.ReactNode;
}) => {
  const dates = getDaysBetweenDates(dateFrom, dateTo);
  const newDates = getDaysBetweenDates(newFromDate, newToDate);
  const newDatesOptions = newDates.map((date) => ({
    value: date.toISOString().split("T")[0],
    label: formatDate(date),
  }));

  return (
    <div>
      {dates.map((date) => {
        const id = date.toISOString().split("T")[0];
        return (
          <div key={uuidv4()} className="mb-1 flex items-center justify-around gap-2">
            <span style={{ width: "calc(50% - 1rem)" }}>
              <input
                value={formatDate(date)}
                disabled
                readOnly
                className="w-full bg-primary-50 p-2 text-sm dark:bg-slate-700"
              />
            </span>
            <div className="w-4">
              <TbArrowBigRightFilled />
            </div>
            <div style={{ width: "calc(50% - 1rem)" }}>
              <PaziaSelect options={newDatesOptions} name={`${id}`} />
            </div>
          </div>
        );
      })}
      <div className="pt-4 text-center text-sm italic text-slate-500">
        Wszystkie wydarzenia z okresu od {formatDate(dateFrom)} do {formatDate(dateTo)} zostaną przeniesione na okres od{" "}
        {formatDate(newFromDate)} do {formatDate(newToDate)} zgodnie z powyższym mapowaniem.
      </div>
      {children}
      <div className="flex justify-end pt-8">
        <PaziaFormButton title="Zapisz zmiany" type="success" />
      </div>
    </div>
  );
};

const TrancheDueDatesMapping = ({
  tranches,
  isSuggested,
  shiftDays,
}: {
  tranches: CourseTrancheApi[];
  isSuggested: (tranche: CourseTrancheApi) => boolean;
  shiftDays: number;
}) => {
  return (
    <div className="pt-6">
      <div className="pb-2 text-center text-sm font-semibold uppercase">Terminy płatności</div>
      {tranches.map((tranche) => (
        <div key={tranche.id} className="mb-1 flex items-center justify-around gap-2">
          <span style={{ width: "calc(50% - 1rem)" }}>
            <input
              value={`${tranche.name}: ${formatDate(tranche.dueDate)}`}
              disabled
              readOnly
              className="w-full bg-primary-50 p-2 text-sm dark:bg-slate-700"
            />
          </span>
          <div className="w-4">
            <TbArrowBigRightFilled />
          </div>
          <div style={{ width: "calc(50% - 1rem)" }}>
            <PaziaDateSelect name={`${FORM_FIELDS.TRANCHE_DUE_DATES}.${trancheFieldKey(tranche.id)}`} />
          </div>
        </div>
      ))}
      <div className="pt-4 text-center text-sm italic text-slate-500">
        {shiftDays !== 0 && !Number.isNaN(shiftDays) && tranches.some(isSuggested)
          ? `Terminy transz przypadające na ten zjazd przesunięto o ${Math.abs(shiftDays)} ${
              Math.abs(shiftDays) === 1 ? "dzień" : "dni"
            } ${shiftDays > 0 ? "później" : "wcześniej"}, tak jak jego start. `
          : ""}
        {tranches.some((t) => !isSuggested(t)) &&
          "Pozostałe transze zachowują termin — zmień go ręcznie w razie potrzeby."}
      </div>
    </div>
  );
};

const startOfDay = (date: Date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const daysBetween = (from: Date, to: Date) =>
  Math.round((startOfDay(to).getTime() - startOfDay(from).getTime()) / (24 * 60 * 60 * 1000));

const addDays = (date: Date, days: number) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

const isSameDay = (a: Date, b: Date) => startOfDay(a).getTime() === startOfDay(b).getTime();

// Noon keeps the calendar day intact through the UTC conversion on the way to the API.
const atNoon = (date: Date) => {
  const d = new Date(date);
  d.setHours(12, 0, 0, 0);
  return d;
};

const getDaysBetweenDates = (dateFrom: Date, dateTo: Date) => {
  const dates = [];
  const currentDate = new Date(dateFrom);
  const stopDate = new Date(dateTo);
  while (currentDate <= stopDate) {
    dates.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }
  return dates;
};
